import { usePrevious } from '@/hooks';
import { mapLayers } from '@/utils/config';
import { getBaseLayers, getMapView } from '@/utils/mapStyles';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ZoomOutMap as ZoomOutMapIcon,
} from '@mui/icons-material';
import { Box, Stack } from '@mui/material';
import { Collection, Feature, Map } from 'ol';
import { defaults as defaultControls } from 'ol/control';
import { altKeyOnly } from 'ol/events/condition';
import { GeoJSON } from 'ol/format';
import {
  MouseWheelZoom as MouseWheelZoomInteraction,
  defaults as defaultInteractions,
} from 'ol/interaction';
import { Vector as VectorLayer } from 'ol/layer';
import 'ol/ol.css';
import { Vector as VectorSource } from 'ol/source';
import { Circle, Fill, Style } from 'ol/style';
import { useEffect, useRef, useState } from 'react';
import { MapButton } from './MapButton';
import { MapLayerSwitcher } from './MapLayerSwitcher';

function defaultStyle(feature) {
  switch (feature.get('name')) {
    case 'point':
      return [
        new Style({
          image: new Circle({
            radius: 8,
            fill: new Fill({
              color: 'rgb(33,150,243)',
            }),
          }),
        }),
        // new Style({
        //   image: new Icon({
        //     src: RouterIcon,
        //     scale: 0.5
        //   })
        // })
      ];
    default:
      return [];
  }
}

export function TelematicsBoxMap({ point }) {
  const [zoomInDisabled, setZoomInDisabled] = useState(false);
  const [zoomOutDisabled, setZoomOutDisabled] = useState(false);
  const prevPoint = usePrevious(point);

  const mapDiv = useRef(null);
  const vectorSource = useRef(null);
  const map = useRef(null);

  function resetMapControls() {
    setZoomInDisabled(
      map.current.getView().getZoom() === map.current.getView().getMaxZoom(),
    );
    setZoomOutDisabled(
      map.current.getView().getZoom() === map.current.getView().getMinZoom(),
    );
  }

  useEffect(() => {
    if (!map.current) {
      vectorSource.current = new VectorSource({
        features: new Collection([]),
      });

      const baseLayers = getBaseLayers(mapLayers);

      map.current = new Map({
        target: mapDiv.current,
        layers: [
          ...baseLayers,
          new VectorLayer({
            source: vectorSource.current,
            style: defaultStyle,
          }),
        ],
        interactions: defaultInteractions({
          mouseWheelZoom: false,
          pinchRotate: false,
          altShiftDragRotate: false,
        }).extend([
          new MouseWheelZoomInteraction({
            condition: altKeyOnly,
          }),
        ]),
        view: getMapView(),
        controls: defaultControls({
          attribution: false,
          rotate: false,
          zoom: false,
        }),
      });
    }
  }, []);

  useEffect(() => {
    vectorSource.current.clear();

    if (point) {
      const geometry = new GeoJSON().readGeometry(point, {
        featureProjection: 'EPSG:3857',
      });

      vectorSource.current.addFeature(
        new Feature({
          geometry,
          name: 'point',
        }),
      );

      if (!prevPoint) {
        map.current.getView().fit(geometry, { maxZoom: 15 });
      } else {
        map.current.getView().setCenter(geometry.getCoordinates());
      }
      resetMapControls();
    }
  }, [point, prevPoint]);

  function handleZoomInClick() {
    if (map.current) {
      map.current.getView().setZoom(map.current.getView().getZoom() + 1);
      resetMapControls();
    }
  }

  function handleZoomOutClick() {
    if (map.current) {
      map.current.getView().setZoom(map.current.getView().getZoom() - 1);
      resetMapControls();
    }
  }

  function handleFitClick() {
    if (vectorSource.current && vectorSource.current.getFeatures().length > 0) {
      map.current
        .getView()
        .fit(vectorSource.current.getExtent(), { maxZoom: 15 });
      resetMapControls();
    }
  }

  return (
    <Box sx={{ position: 'relative', width: 1, height: 1 }}>
      <Box id="map" sx={{ width: 1, height: 1 }} ref={mapDiv} />
      <Stack sx={{ position: 'absolute', top: 8, left: 8 }}>
        <MapButton
          title="Zoom In"
          aria-label="Zoom In"
          disabled={zoomInDisabled}
          onClick={handleZoomInClick}
          size="large"
        >
          <ZoomInIcon />
        </MapButton>
        <MapButton
          title="Zoom Out"
          aria-label="Zoom Out"
          disabled={zoomOutDisabled}
          onClick={handleZoomOutClick}
          size="large"
        >
          <ZoomOutIcon />
        </MapButton>
        <MapButton
          title="Fit"
          aria-label="Fit"
          onClick={handleFitClick}
          size="large"
        >
          <ZoomOutMapIcon />
        </MapButton>
      </Stack>
      <MapLayerSwitcher mapRef={map} />
    </Box>
  );
}
